import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useState, useEffect } from "react";
import { useSubscription } from "@/contexts/SubscriptionContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { resetRating, updateAttendanceTrackingSystem, toggleEmployeePortal } from "@/services/adminService";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Trash2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import ConfirmDialog from "@/components/ConfirmDialog";
import { usePermission } from "@/contexts/RoleAccessContext";
import { createCanHelpers } from "@/utils/rbac";

const ConfigurationsTab = ({
  payrollSettings,
  handlePayrollSettingChange,
  ratingConfig,
  setRatingConfig,
  setCompanyEmployeePortalEnabled,
  companyEmployeePortalEnabled,
}) => {
  const { can } = usePermission();
  const { canView, canEdit } = createCanHelpers(can);

  const canViewPolicy = canView("attendance_and_policy_configuration");
  const canEditPolicy = canEdit("attendance_and_policy_configuration");

  const overtime = payrollSettings.overtime || {
    enabled: false,
    method: "fixed",
    rate: 0,
  };
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmData, setConfirmData] = useState({
    title: "",
    message: "",
    onConfirm: () => {},
  });
  const [ratingData, setRatingData] = useState({
    base_rating: "",
    penalty_late: "",
    reward_overtime: "",
    punctuality_bonus: "",
    max_rating: "",
    reset_annually: true,
  });
  const { hasFeature } = useSubscription();

  const openConfirm = (cfg) => {
    setConfirmData(cfg);
    setConfirmOpen(true);
  };

  useEffect(() => {
    if (ratingConfig) {
      setRatingData({
        base_rating: ratingConfig.base_rating || "",
        penalty_late: ratingConfig.late_arrival || "",
        reward_overtime: ratingConfig.overtime || "",
        punctuality_bonus: ratingConfig.punctuality_bonus || "",
        max_rating: ratingConfig.max_rating || "",
        reset_annually: ratingConfig.reset_annually ?? true,
      });
    }
  }, [ratingConfig]);

  const handleDecimalInput = (key, value) => {
    if (value === "") {
      handleRatingChange(key, "");
      return;
    }

    // Allow only numbers with max 2 decimals
    const regex = /^\d{0,2}(\.\d{0,2})?$/;

    if (!regex.test(value)) return;

    let num = parseFloat(value);

    if (num > 10) num = 10;

    handleRatingChange(key, num);
  };

  const handleRatingChange = (key, value) => {
    // 1️⃣ Update local UI state
    setRatingData((prev) => ({
      ...prev,
      [key]: value,
    }));

    // 2️⃣ Map UI keys → backend schema keys
    const keyMap = {
      penalty_late: "late_arrival",
      reward_overtime: "overtime",
    };

    const backendKey = keyMap[key] || key;

    setRatingConfig((prev) => ({
      ...prev,
      [backendKey]: value,
    }));
  };

  const CheckInCard = () => {
    const [requiresPunch, setRequiresPunch] = useState(
      payrollSettings.isCheckinOutEnabled
    );
    const [showEarlyCheckoutDialog, setShowEarlyCheckoutDialog] =
      useState(false);
    const [pendingValue, setPendingValue] = useState(null);

    const statusText = requiresPunch ? "Active" : "Disabled";
    const statusColor = requiresPunch ? "text-green-600" : "text-yellow-600";

    const handleSwitchChange = async () => {
      if (pendingValue === null) return;

      try {
        if (!canEditPolicy) {
          return canEdit("attendance_and_policy_configuration", true);
        }
        await updateAttendanceTrackingSystem({
          require_daily_punch: pendingValue,
        });

        // ✅ persist locally
        setRequiresPunch(pendingValue);

        toast.success("Attendance tracking setting updated");
      } catch (error) {
        toast.error("Failed to update attendance settings");
      } finally {
        setShowEarlyCheckoutDialog(false);
      }
    };

    return (
      <>
        <Card id="setting-rating/punch">
          <CardContent className="flex items-center justify-between space-x-4 p-4 rounded-lg ">
            <div className="flex flex-col">
              <Label htmlFor="daily-punch">Require Daily Punch In/Out</Label>

              <p className="text-sm text-gray-500 mt-1">
                Employees must clock in at the start and clock out at the end of
                every shift.
              </p>

              <div className="mt-2 flex items-center space-x-2">
                <span className="text-xs font-semibold uppercase">Status:</span>
                <span className={`text-sm font-medium ${statusColor}`}>
                  {statusText}
                </span>
              </div>
            </div>

            <Switch
              checked={requiresPunch}
              disabled={!canEditPolicy}
              onCheckedChange={(checked) => {
                if (!canEditPolicy)
                  return canEdit("attendance_and_policy_configuration", true);
                setPendingValue(checked);
                setShowEarlyCheckoutDialog(true);
              }}
            />
          </CardContent>
        </Card>
        <Dialog
          open={showEarlyCheckoutDialog}
          onOpenChange={setShowEarlyCheckoutDialog}
        >
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-red-600">
                Change Attendance Tracking Settings?
              </DialogTitle>

              <DialogDescription className="mt-2">
                You are about to modify the attendance punch-in / punch-out
                system.
              </DialogDescription>
            </DialogHeader>

            <div className="text-sm text-muted-foreground space-y-3 mt-2">
              <p>
                This change can have <strong>cascading effects</strong> across
                the system, including:
              </p>

              <ul className="list-disc pl-5 space-y-1">
                <li>Attendance records and working hours calculations</li>
                <li>Late arrival and early checkout rules</li>
                <li>Overtime eligibility and overtime calculations</li>
                <li>Payroll processing and salary computation</li>
                <li>Compliance reports and audit logs</li>
              </ul>

              <p className="pt-2">
                This setting should only be changed if you fully understand the
                impact on attendance and payroll data.
              </p>

              <p className="font-medium text-foreground">
                Are you sure you want to proceed?
              </p>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowEarlyCheckoutDialog(false)}
              >
                Cancel
              </Button>

              <Button
                variant="destructive"
                onClick={() => handleSwitchChange()}
              >
                Yes, Change Settings
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  };

  const EmployeePortalCard = () => {
    const [saving, setSaving] = useState(false);
    const [sendInvites, setSendInvites] = useState(true);

    const [showDialog, setShowDialog] = useState(false);
    const [pendingValue, setPendingValue] = useState(null);

    const statusText = companyEmployeePortalEnabled ? "Active" : "Disabled";
    const statusColor = companyEmployeePortalEnabled ? "text-green-600" : "text-yellow-600";

    const handleConfirm = async () => {
      if (pendingValue === null) return;

      try {
        if (!canEditPolicy) {
          return canEdit("attendance_and_policy_configuration", true);
        }
        setSaving(true);
        const res = await toggleEmployeePortal({ enabled: pendingValue, sendInvites });

        setCompanyEmployeePortalEnabled(pendingValue);
        
        toast.success(res?.data?.message);
      } catch (err) {
        toast.error("Failed to update employee portal setting");
      } finally {
        setSaving(false);
        setShowDialog(false);
      }
    };

    const isEnabling = pendingValue === true;
    const isDisabling = pendingValue === false;

    return (
      <>
        <Card id="setting-employee-portal">
          <CardContent className="flex items-center justify-between space-x-4 p-4">
            <div className="flex flex-col">
              <Label>Enable Employee Portal</Label>

              <p className="text-sm text-muted-foreground mt-1">
                Allow employees to login, view payslips, apply leaves, and
                manage their profile.
              </p>

              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs font-semibold uppercase">Status:</span>
                <span className={`text-sm font-medium ${statusColor}`}>
                  {statusText}
                </span>
              </div>
            </div>

            <Switch
              checked={companyEmployeePortalEnabled}
              disabled={!canEditPolicy}
              onCheckedChange={(checked) => {
                if (!canEditPolicy)
                  return canEdit("attendance_and_policy_configuration", true);
                setPendingValue(checked);
                setShowDialog(true);
              }}
            />
          </CardContent>
        </Card>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {isEnabling
                  ? "Enable Employee Portal?"
                  : "Disable Employee Portal?"}
              </DialogTitle>

              <DialogDescription>
                {isEnabling
                  ? "Enabling the portal will create employee accounts and send invitation emails."
                  : "Disabling the portal will block employees from logging into their accounts."}
              </DialogDescription>
            </DialogHeader>

            <div className="text-sm text-muted-foreground space-y-3 mt-2">
              {isEnabling && (
                <>
                  <p>This will allow employees to:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Log in to the portal</li>
                    <li>Apply & track leave requests</li>
                    <li>View payslips and salary details</li>
                    <li>Update personal information</li>
                  </ul>
                </>
              )}

              {isDisabling && (
                <p className="font-medium text-red-600">
                  Employees will immediately lose access to the portal.
                </p>
              )}

              {isEnabling && (
                <div className="mt-4 flex items-start gap-2">
                  <Checkbox
                    checked={sendInvites}
                    onCheckedChange={(checked) => setSendInvites(checked)}
                    className="mt-1"
                    id="send_invite"
                  />

                  <Label htmlFor="send_invite" className="text-sm">
                    Send invitation email to employees who do not have access
                    yet.
                    <br />
                    <span className="text-muted-foreground">
                      Already activated employees will be skipped automatically.
                    </span>
                  </Label>
                </div>
              )}

              <p className="pt-2 font-medium text-foreground">
                Do you want to continue?
              </p>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowDialog(false)}
                disabled={saving}
              >
                Cancel
              </Button>

              <Button
                onClick={handleConfirm}
                variant={isDisabling ? "destructive" : "default"}
                disabled={saving}
              >
                {saving ? (
                  <span className="flex items-center gap-2">
                    Processing…
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </span>
                ) : isEnabling ? (
                  "Yes, Enable Portal"
                ) : (
                  "Yes, Disable Portal"
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  };

 const handleResetRating = async () => {
  try {
    if (!canEditPolicy) {
      return canEdit("attendance_and_policy_configuration", true);
    }
    const res = await resetRating();
    toast.success(res?.data?.message || "Ratings reset successfully.");
  } catch (err) {
    console.error(err);
    toast.error(
        "Failed to reset ratings. Please try again."
    );
  }
};


  return (
    <>
      <div className="grid gap-6 lg:grid-cols-2">
        {hasFeature("employee_portal") && <EmployeePortalCard />}
        {hasFeature("performance_ratings") && <CheckInCard />}
      </div>

      <Card id="setting-overtime">
        <CardHeader>
          <CardTitle>Overtime Configuration</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Control whether overtime hours are included in payroll and how they
            are calculated.
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* ================= OVERTIME CONFIGURATION ================= */}
          <div
            id="setting-payroll-overtime"
            className="space-y-4 rounded-lg p-2"
          >
            {/* Toggle */}
            <div className="flex items-center justify-between">
              <Label>Include Overtime in Payroll</Label>
              <Switch
                checked={overtime.enabled}
                disabled={!canEditPolicy}
                onCheckedChange={(v) => {
                  if (!canEditPolicy)
                    return canEdit("attendance_and_policy_configuration", true);
                  handlePayrollSettingChange("overtime", {
                    ...overtime,
                    enabled: v,
                  });
                }}
              />
            </div>

            {/* Conditional Fields */}
            {overtime.enabled && (
              <div className="space-y-4 rounded-md bg-muted/50 p-4">
                {/* Method */}
                <div>
                  <Label className="text-sm font-medium">
                    Overtime Calculation Method
                  </Label>

                  <RadioGroup
                    disabled={!canEditPolicy}
                    value={overtime.method}
                    onValueChange={(v) =>
                      handlePayrollSettingChange("overtime", {
                        ...overtime,
                        method: v,
                      })
                    }
                    className="mt-2 space-y-2"
                  >
                    <div className="flex items-center space-x-3">
                      <RadioGroupItem value="fixed" id="ot_fixed" />
                      <Label htmlFor="ot_fixed" className="cursor-pointer">
                        Fixed amount per hour
                      </Label>
                    </div>

                    <div className="flex items-center space-x-3">
                      <RadioGroupItem value="percentage" id="ot_percentage" />
                      <Label htmlFor="ot_percentage" className="cursor-pointer">
                        Percentage of hourly wage
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Rate */}
                <div>
                  <Label className="text-sm font-medium">
                    {overtime.method === "percentage"
                      ? "Overtime Rate (%)"
                      : "Overtime Rate (₹ per hour)"}
                  </Label>

                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder={
                      overtime.method === "percentage" ? "e.g. 150" : "e.g. 200"
                    }
                    value={overtime.rate || ""}
                    disabled={!canEditPolicy}
                    onChange={(e) =>
                      handlePayrollSettingChange("overtime", {
                        ...overtime,
                        rate: Number(e.target.value),
                      })
                    }
                    className="mt-1"
                  />

                  <p className="text-xs text-muted-foreground mt-1">
                    {overtime.method === "percentage"
                      ? "Percentage applied on the employee’s hourly wage."
                      : "Flat amount paid per overtime hour."}
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {hasFeature("performance_ratings") && (
        <div id="setting-notification-rating">
          <Card id="setting-rating">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Rating Configuration</CardTitle>

              <Button
                size="sm"
                variant="outline"
                className="text-destructive border-destructive/20 hover:bg-destructive/10 hover:text-destructive transition-colors"
                disabled={!canEditPolicy}
                onClick={() => {
                  if (!canEditPolicy)
                    return canEdit("attendance_and_policy_configuration", true);
                  openConfirm({
                    title: "Reset All Employee Ratings",
                    message:
                      "This will restore all employee ratings to the base value. This action is permanent.",
                    onConfirm: handleResetRating,
                  });
                }}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Reset All Ratings
              </Button>

            </CardHeader>

            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex flex-col space-y-1">
                  <Label>Base Rating</Label>
                  <Input
                    type="number"
                    disabled={!canEditPolicy}
                    step="0.01"
                    min="0"
                    max="10"
                    onWheel={(e) => e.target.blur()}
                    value={ratingData.base_rating}
                    onChange={(e) =>
                      handleDecimalInput("base_rating", e.target.value)
                    }
                  />
                </div>

                <div className="flex flex-col space-y-1">
                  <Label>Penalty Late</Label>
                  <Input
                    type="number"
                    disabled={!canEditPolicy}
                    step="0.01"
                    min="0"
                    max="10"
                    onWheel={(e) => e.target.blur()}
                    value={ratingData.penalty_late}
                    onChange={(e) =>
                      handleDecimalInput("penalty_late", e.target.value)
                    }
                  />
                </div>

                <div className="flex flex-col space-y-1">
                  <Label>Overtime Reward Points (Per 30 Minutes)</Label>
                  <Input
                    type="number"
                    disabled={!canEditPolicy}
                    step="0.01"
                    min="0"
                    max="10"
                    onWheel={(e) => e.target.blur()}
                    value={ratingData.reward_overtime}
                    onChange={(e) =>
                      handleDecimalInput("reward_overtime", e.target.value)
                    }
                  />
                </div>

                <div className="flex flex-col space-y-1">
                  <Label>Bonus (Zero Late)</Label>
                  <Input
                    type="number"
                    disabled={!canEditPolicy}
                    step="0.01"
                    min="0"
                    max="10"
                    onWheel={(e) => e.target.blur()}
                    value={ratingData.punctuality_bonus}
                    onChange={(e) =>
                      handleDecimalInput("punctuality_bonus", e.target.value)
                    }
                  />
                </div>

                <div className="flex flex-col space-y-1">
                  <Label>Max Rating</Label>
                  <Select
                    disabled={!canEditPolicy}
                    value={String(ratingData.max_rating)}
                    onValueChange={(value) =>
                      handleRatingChange("max_rating", Number(value))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select max rating" />
                    </SelectTrigger>

                    <SelectContent>
                      <SelectItem value="5">5</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="col-span-3 flex items-center justify-start gap-x-3">
                  <Label>Annual Reset (Jan)</Label>
                  <Switch
                    disabled={!canEditPolicy}
                    checked={ratingData.reset_annually}
                    onCheckedChange={(checked) =>
                      handleRatingChange("reset_annually", checked)
                    }
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={confirmData.title}
        message={confirmData.message}
        onConfirm={confirmData.onConfirm}
      />
    </>
  );
};

export default ConfigurationsTab;
